import Layout from '../../components/Layout';
import React, {useRef, useState, useEffect} from 'react';
import {DateTime} from 'luxon';
import {cloneDeep} from 'lodash';
import {StepStatus, useAppContext} from '../../context/app-context';
import {useChartDataContext} from './context/ChartDataContext';
import {useAuthContext, useClient} from '../../context/auth-context';
import ErrorMessageBar from '../../components/ErrorMessageBar';
import CustomNode from './components/CustomNode';
import TextDialog from '@gakki/components/Dialog/TextDialog';
import {
  ResetDialog,
  ResetSuccessDialog,
  FailDialog,
} from '../../components/Dialog';
import {ReactComponent as Warning} from '../../assets/warning.svg';
import {ReactComponent as CirclePlus} from '../../assets/circle_plus.svg';
import {ReactComponent as CircleMinus} from '../../assets/circle_minus.svg';
import {ReactComponent as RedoIcon} from '@common/assets/icon_redo.svg';
import {useMutation} from 'react-query';
import cx from 'classnames';
import OrganizationChart from './components/OrgChart/ChartContainer';
import Img from '@common/assets/img_org.svg';
import './style.css';

export const STEP = 3;
export default function StepOrgData() {
  const api = useClient();
  const {companyId} = useAuthContext();
  const {
    dataSource,
    resetChart,
    setIsConfirmed,
    dragNode,
    validate,
    errorMessages,
  } = useChartDataContext();
  const {
    changeStep,
    updatedTime,
    stepStatus,
    save,
    setIsLoading,
    isLoading,
    saveAndGoStep,
    saveFail,
    confirm,
    refetchEnablePY,
    onPrev,
  } = useAppContext();

  const chartRef = useRef();
  const [chartScale, setChartScale] = useState(1);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [showResetSuccessDialog, setShowResetSuccessDialog] = useState(false);
  const [showFailDialog, setShowFailDialog] = useState(false);
  const [showTextDialog, setShowTextDialog] = useState(false);
  const saveData = () => {
    return api.put(`organization/company/${companyId}`, {
      data: dataSource.children,
    });
  };

  const confirmData = () => {
    return api.put(`organization/company/${companyId}/confirm`);
  };
  const {mutate: saveMutate, isLoading: isSaveLoading} = useMutation(saveData, {
    onSuccess: () => save(),
    onError: () => saveFail(),
    onMutate: () => setIsLoading(true),
    onSettled: () => setIsLoading(false),
  });

  const {mutate: handleConfirm, isLoading: isConfirmLoading} = useMutation(
    () =>
      saveData()
        .then(() => refetchEnablePY('next', STEP + 1))
        .then(async () => {
          try {
            await confirmData();
            setIsLoading(false);
            changeStep(STEP + 1);
            confirm();
          } catch (e) {
            throw new Error(e);
          }
        }),
    {
      onError: () => {
        setIsLoading(false);
        setShowFailDialog(true);
      },
      onMutate: () => setIsLoading(true),
    },
  );

  useEffect(() => {
    if (typeof saveAndGoStep === 'number') {
      saveMutate();
    }
  }, [saveAndGoStep]);

  const handleNext = () => {
    const _dataSource = cloneDeep(dataSource);
    const hasError = validate(_dataSource);
    setIsConfirmed(true);
    if (hasError) {
      setShowFailDialog(true);
    } else {
      handleConfirm();
    }
  };

  const exportPDF = async () => {
    await chartRef.current.exportTo(
      `組織圖_${DateTime.now().toFormat('yyyy/MM/dd')}`,
      'png',
    );
  };

  const handleReset = () => {
    resetChart();
    setShowResetDialog(false);
    setShowResetSuccessDialog(true);
  };

  const handleChartScale = e => {
    const {dataset} = e.currentTarget;
    let scale = chartScale;
    if (dataset.scale === 'minus') {
      if (chartScale > 0.25) {
        if (chartScale > 3) {
          scale = chartScale - 1;
        } else if (chartScale > 1) {
          scale = chartScale - 0.5;
        } else if (chartScale > 0.25) {
          scale = chartScale - 0.25;
        }
      }
    }
    if (dataset.scale === 'plus') {
      if (chartScale < 5) {
        if (chartScale >= 3) {
          scale = chartScale + 1;
        } else if (chartScale >= 1) {
          scale = chartScale + 0.5;
        } else if (chartScale >= 0.25) {
          scale = chartScale + 0.25;
        }
      }
    }
    setChartScale(scale);
  };

  let chartScaleClass = 'scale4';
  switch (chartScale) {
    case 0.25:
      chartScaleClass = 'scale1';
      break;
    case 0.5:
      chartScaleClass = 'scale2';
      break;
    case 0.75:
      chartScaleClass = 'scale3';
      break;
    case 1:
      chartScaleClass = 'scale4';
      break;
    case 1.5:
      chartScaleClass = 'scale5';
      break;
    case 2:
      chartScaleClass = 'scale6';
      break;
    case 2.5:
      chartScaleClass = 'scale7';
      break;
    case 3:
      chartScaleClass = 'scale8';
      break;
    case 4:
      chartScaleClass = 'scale9';
      break;
    case 5:
      chartScaleClass = 'scale10';
      break;
    default:
      chartScaleClass = 'scale4';
  }

  return (
    <React.Fragment>
      <div className="relative" data-test-id="view-chart">
        <OrganizationChart
          containerClass={`viewable ${chartScaleClass} orgchart-container ${
            errorMessages.length > 0 ? 'error' : ''
          }`}
          collapsible={false}
          datasource={dataSource}
          NodeTemplate={CustomNode}
          draggable={true}
          onDropChart={dragNode}
        />
        <div
          className={cx(
            'fixed left-[35px]',
            errorMessages.length > 0 ? 'bottom-[134px]' : 'bottom-[74px]',
            'flex items-start flex-col gap-[10px]',
          )}
          data-test-id="view-chart"
        >
          <div
            className={cx(
              'z-[5] h-[34px] w-[270px] pr-[25.5px] bg-white select-none',
              'flex justify-between items-center',
              'border border-[#e3e4e6] rounded-[100px]',
            )}
          >
            <div
              className={
                'text-lg w-[147px] flex justify-between items-center ml-[-3px]'
              }
            >
              <CircleMinus
                aria-label="zoom-out"
                data-scale="minus"
                className={cx('org-zoom-out-btn', {
                  'cursor-pointer': chartScale > 0.25,
                  'cursor-normal': chartScale <= 0.25,
                  'opacity-100': chartScale > 0.25,
                  'opacity-50': chartScale <= 0.25,
                })}
                onClick={handleChartScale}
              />
              <span className="text-[#26abe3]">{`${chartScale * 100}%`}</span>
              <CirclePlus
                aria-label="zoom-in"
                className={cx('org-zoom-in-btn', {
                  'cursor-pointer': chartScale < 5,
                  'cursor-normal': chartScale >= 5,
                  'opacity-100': chartScale < 5,
                  'opacity-50': chartScale >= 5,
                })}
                data-scale="plus"
                onClick={handleChartScale}
              />
            </div>
            <div
              className={
                'flex justify-center items-center reset-btn text-base pl-[12.6px] cursor-pointer border-l border-[rgba(198, 199, 200, 0.39)]'
              }
              onClick={() => setShowResetDialog(true)}
              aria-label="reset-org"
            >
              <RedoIcon className={'mr-[5px]'} />
              全部重設
            </div>
          </div>
          <div className="flex text-base gap-[15px]">
            <div className="flex items-center">
              文字顏色說明：
              <div className="w-[7px] h-[7px] inline-block rounded-full bg-primary mx-[5px]" />
              部門與人數
              <span className="mx-[8px]">｜</span>
              <div className="w-[7px] h-[7px] inline-block rounded-full bg-[#fb782d] mr-[5px]" />
              正主管
              <span className="mx-[8px]">｜</span>
              <div className="w-[7px] h-[7px] inline-block rounded-full bg-black mr-[5px]" />
              副主管
            </div>
            <div
              className="text-[#26abe3] cursor-pointer hover:underline"
              onClick={() => setShowTextDialog(true)}
            >
              組織圖說明與範例
            </div>
          </div>
        </div>
      </div>
      {errorMessages.length > 0 && (
        <ErrorMessageBar>
          <Warning className={'text-[38px] mr-[13.8px]'} />
          {errorMessages.join('、')}
        </ErrorMessageBar>
      )}
      <Layout.Footer
        showNextButton
        showPrevButton
        showSaveButton
        showDownloadOrgChartButton
        onPrev={onPrev}
        onNext={handleNext}
        onSave={saveMutate}
        onDownloadOrgChart={exportPDF}
        updatedTime={updatedTime}
        disabledSaveButton={stepStatus !== StepStatus.edit || isLoading}
        disabledPrevButton={stepStatus === StepStatus.edit || isLoading}
        disabledNextButton={isLoading}
      />
      <ResetDialog
        text={
          <span>
            全部重置後，
            <br />
            既有組織設定將全數被清空，
            <br />
            請問您依舊要全部重置嗎？
          </span>
        }
        isOpen={showResetDialog}
        onCancel={() => setShowResetDialog(false)}
        onConfirm={handleReset}
      />
      <ResetSuccessDialog
        isOpen={showResetSuccessDialog}
        onCancel={() => setShowResetSuccessDialog(false)}
      />
      <FailDialog
        isOpen={showFailDialog}
        onConfirm={() => setShowFailDialog(false)}
        onCancel={() => setShowFailDialog(false)}
      />
      <TextDialog
        isOpen={showTextDialog}
        onConfirm={() => setShowTextDialog(false)}
        onCancel={() => setShowTextDialog(false)}
        title={'組織圖說明與範例'}
        content={
          <div className="flex flex-col items-center pl-[22px] mt-[-10px]">
            <ul className="list-decimal">
              <li>
                <p className="text-[15px] leading-8">
                  組織圖主要影響簽核。若員工送出假單，會先簽核主管A(直屬主管)、再簽核主管B(上層主管)；則組織圖由上而下的架構為「主管B
                  → 主管A → 員工」
                </p>
              </li>
              <li>
                <p className="text-[15px] leading-8">
                  組織架構圖舉例最高層為總經理室，旗下有 3
                  個平行部門為：行政部、設計部、產品規劃部，而設計部旗下又有平面設計部、互動設計部。
                  <br /> 第一層：總經理室
                  <br /> 第二層：行政部、設計部、產品規劃部
                  <br /> 第三層：平面設計部、互動設計部
                </p>
              </li>
            </ul>
            <img alt="orgImg" src={Img} className="mt-5 mb-10 w-[450px]" />
          </div>
        }
      />
      <OrganizationChart
        containerClass={'hide orgchart-container'}
        ref={chartRef}
        collapsible={false}
        datasource={dataSource}
        NodeTemplate={CustomNode}
      >
        <div
          className={cx(
            'absolute right-[15px] bottom-[15px]',
            'flex text-base items-center',
          )}
        >
          文字顏色說明：
          <div className="inline-block h-[20px] pt-[4px] mx-[5px]">
            <div className="w-[7px] h-[7px] inline-block rounded-full bg-primary" />
          </div>
          部門與人數
          <span className="mx-[8px]">｜</span>
          <div className="inline-block h-[20px] pt-[4px] mr-[5px]">
            <div className="w-[7px] h-[7px] inline-block rounded-full bg-[#fb782d]" />
          </div>
          正主管
          <span className="mx-[8px]">｜</span>
          <div className="inline-block h-[20px] pt-[4px] mr-[5px]">
            <div className="w-[7px] h-[7px] inline-block rounded-full bg-black" />
          </div>
          副主管
        </div>
      </OrganizationChart>
    </React.Fragment>
  );
}
